import { zrpc } from "@/lib/zrpc";
import { QueryClient } from "@tanstack/react-query";

/**
 * Invalidates React Query cache for interface-related queries.
 * Call this after any operation that modifies interface data to ensure UI stays in sync.
 *
 * @param client - The QueryClient instance
 * @param interfaceId - The ID of the interface that was modified
 */
export async function invalidateInterfaceQueries(
  client: QueryClient,
  interfaceId: string
): Promise<void> {
  const getInterfaceQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}",
    {
      params: {
        path: {
          interface_id: interfaceId,
        },
      },
    }
  );
  const getInterfacesQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces",
    {}
  );

  await Promise.all([
    client.invalidateQueries(getInterfaceQueryOptions),
    client.invalidateQueries(getInterfacesQueryOptions),
  ]);
}

/**
 * Invalidates React Query cache for interface page-related queries.
 * Call this after any operation that modifies page data to ensure UI stays in sync.
 *
 * @param client - The QueryClient instance
 * @param interfaceId - The ID of the interface that was modified
 * @param pageId - The ID of the page that was modified
 */
export async function invalidatePageQueries({
  client,
  interfaceId,
  pageId,
}: {
  client: QueryClient;
  interfaceId: string;
  pageId: string;
}): Promise<void> {
  const listPagesQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages",
    {
      params: {
        path: {
          interface_id: interfaceId,
        },
      },
    }
  );
  const getPageQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}",
    {
      params: {
        path: {
          interface_id: interfaceId,
          page_id: pageId,
        },
      },
    }
  );

  await Promise.all([
    client.invalidateQueries(listPagesQueryOptions),
    client.invalidateQueries(getPageQueryOptions),
  ]);
}
